import axios from 'axios'
import {
  getCompanyApiBaseUrl,
  getCurrentApiKey,
  getCurrentTenantId,
  getDefaultRequestHeaders,
  hasAuthenticatedSession,
  hasUsableAuthenticatedSession
} from './session'
import {
  redirectToLoginWithSessionNotice,
  recoverAuthenticatedSession,
  SessionExpiredError
} from './authFlow'

const isAuthStatus = (status?: number): boolean => status === 401 || status === 403

const attachSessionHeaders = (headers?: HeadersInit): Headers => {
  const mergedHeaders = new Headers(headers || {})
  const sessionHeaders = getDefaultRequestHeaders()

  Object.entries(sessionHeaders).forEach(([key, value]) => {
    if (value && !mergedHeaders.has(key)) {
      mergedHeaders.set(key, value)
    }
  })

  return mergedHeaders
}

export const fetchWithApiKey = async (
  url: string,
  options: RequestInit = {},
  hasRetriedAuth = false
): Promise<Response> => {
  if (hasAuthenticatedSession() && !hasUsableAuthenticatedSession()) {
    const recovered = await recoverAuthenticatedSession()
    if (!recovered) {
      throw new SessionExpiredError()
    }
  }

  try {
    const response = await window.fetch(url, {
      ...options,
      headers: attachSessionHeaders(options.headers)
    })

    if (isAuthStatus(response.status)) {
      if (!hasRetriedAuth && hasAuthenticatedSession()) {
        const recovered = await recoverAuthenticatedSession()

        if (recovered) {
          return fetchWithApiKey(url, options, true)
        }
      }

      redirectToLoginWithSessionNotice()
      throw new SessionExpiredError()
    }

    return response
  } catch (error: any) {
    if (
      !hasRetriedAuth &&
      hasAuthenticatedSession() &&
      error instanceof TypeError &&
      `${error.message || ''}`.toLowerCase().includes('failed to fetch')
    ) {
      const recovered = await recoverAuthenticatedSession()

      if (recovered) {
        return fetchWithApiKey(url, options, true)
      }

      throw new SessionExpiredError()
    }

    throw error
  }
}

export const apiCompany = axios.create({
  baseURL: getCompanyApiBaseUrl()
})

apiCompany.interceptors.request.use(async (config) => {
  if (hasAuthenticatedSession() && !hasUsableAuthenticatedSession()) {
    const recovered = await recoverAuthenticatedSession()

    if (!recovered) {
      return Promise.reject(new SessionExpiredError())
    }
  }

  config.headers = config.headers || {}

  const apiKey = getCurrentApiKey()
  const tenantId = getCurrentTenantId()

  if (apiKey && !config.headers['x-api-key']) {
    config.headers['x-api-key'] = apiKey
  }

  if (tenantId && !config.headers['x-tenant-id']) {
    config.headers['x-tenant-id'] = tenantId
  }

  if (!config.headers['x-application']) {
    config.headers['x-application'] = 'dash_contingencia'
  }

  return config
})

apiCompany.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (
      isAuthStatus(error?.response?.status) &&
      !error?.config?.__authRetry
    ) {
      if (hasAuthenticatedSession()) {
        const recovered = await recoverAuthenticatedSession()

        if (recovered) {
          const retryConfig = {
            ...error.config,
            __authRetry: true
          }

          return apiCompany(retryConfig)
        }
      }

      redirectToLoginWithSessionNotice()
      return Promise.reject(new SessionExpiredError())
    }

    if (isAuthStatus(error?.response?.status)) {
      redirectToLoginWithSessionNotice()
      return Promise.reject(new SessionExpiredError())
    }

    return Promise.reject(error)
  }
)
